import React, { useCallback, useMemo, useRef } from 'react';
import Box from '../box';
import HorizontalStack from '../horizontal-stack';
import SimpleSelect from '../simple-select';
import GradientStopsSlider from './components/gradient-stops-slider';
import GradientStopActions from './components/gradient-stop-actions';
import ColorStopControlPanel from './components/color-stop-control-panel';
import LinearGradientAnglePicker from './components/linear-gradient-angle-picker';
import RadialGradientCenterPicker from './components/radial-gradient-center-picker';
import { colorStopsManager } from './helpers/color-stops-manager';
import { gradientModelManager } from './helpers/gradient-model-manager';
import { interpolateHslColorAtPosition } from './helpers/interpolate-hsl-color-at-position';
import { useBackground } from './hooks/use-background';
import { useColorStopPanelArrowPosition } from './hooks/use-color-stop-panel-arrow-position';
import { useGradientModel } from './hooks/use-gradient-model';

const GradientPicker = ({
  defaultValue,
  captions,
  contrastRatioChecker,
  onChangeStart,
  onChange,
  onChangeEnd,
  onActiveColorClick = undefined,
}) => {
  const { model, activeColorStop, handleModelChange } = useGradientModel({
    defaultValue,
    onChangeStart,
    onChange,
    onChangeEnd,
  });
  const pickerRef = useRef(null);
  const sliderMeasureRef = useRef(null);
  const panelMeasureRef = useRef(null);

  const isLinearVariant = model.type === 'linear-gradient';
  const linearAngle = gradientModelManager.extractLinearAngle(model);
  const radialPosition = gradientModelManager.extractRadialPosition(model);

  const sliderStops = useMemo(
    () =>
      model.colorStops.map((stop) => ({
        id: stop.id,
        position: gradientModelManager.extractStopPosition(stop),
      })),
    [model.colorStops]
  );

  const isAnglePickerDarkTheme = useMemo(() => {
    if (!isLinearVariant) {
      return false;
    }

    const linearCenterColor = interpolateHslColorAtPosition(50, model.colorStops);

    return Boolean(contrastRatioChecker(gradientModelManager.buildHslaValue(linearCenterColor)));
  }, [contrastRatioChecker, isLinearVariant, model.colorStops]);

  const { previewBackground, sliderBackground } = useBackground(model);
  const arrowPosition = useColorStopPanelArrowPosition({
    pickerRef,
    sliderRef: sliderMeasureRef,
    panelRef: panelMeasureRef,
    positionPercent: activeColorStop
      ? gradientModelManager.extractStopPosition(activeColorStop)
      : undefined,
  });

  const handleGradientTypeChange = useCallback(
    (value) => {
      if (value == null) {
        return;
      }

      const nextType = value.value;

      if (nextType === model.type) {
        return;
      }

      handleModelChange({
        patch: (prev) => gradientModelManager.switchType(prev, nextType) ?? {},
        reason: 'change-type',
        phase: 'end',
      });
    },
    [handleModelChange, model.type]
  );

  const handleStopsDragStart = useCallback(
    ({ stopId }) => {
      handleModelChange({
        patch: (prev) => colorStopsManager.activate(prev.colorStops, stopId),
        reason: 'move-stop',
        phase: 'start',
      });
    },
    [handleModelChange]
  );

  const handleStopsDrag = useCallback(
    ({ stopId, position }) => {
      handleModelChange({
        patch: (prev) => colorStopsManager.move(prev.colorStops, stopId, position),
        reason: 'move-stop',
        phase: 'change',
      });
    },
    [handleModelChange]
  );

  const handleStopsDragEnd = useCallback(
    ({ order }) => {
      handleModelChange({
        patch: (prev) => colorStopsManager.reorder(prev.colorStops, order),
        reason: 'move-stop',
        phase: 'end',
      });
    },
    [handleModelChange]
  );

  const handleRailClick = useCallback(
    ({ position }) => {
      handleModelChange({
        patch: (prev) => colorStopsManager.create(prev.colorStops, position),
        reason: 'add-stop',
        phase: 'end',
      });
    },
    [handleModelChange]
  );

  const handleLinearAngleDragStart = useCallback(
    ({ value }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildAngularOrientation(value) },
        reason: 'change-linear-angle',
        phase: 'start',
      });
    },
    [handleModelChange]
  );

  const handleLinearAngleDrag = useCallback(
    ({ value }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildAngularOrientation(value) },
        reason: 'change-linear-angle',
        phase: 'change',
      });
    },
    [handleModelChange]
  );

  const handleLinearAngleCommit = useCallback(
    ({ value }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildAngularOrientation(value) },
        reason: 'change-linear-angle',
        phase: 'end',
      });
    },
    [handleModelChange]
  );

  const handleRadialCenterDragStart = useCallback(
    ({ position }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildShapeOrientation(position, 'circle') },
        reason: 'change-radial-position',
        phase: 'start',
      });
    },
    [handleModelChange]
  );

  const handleRadialCenterDrag = useCallback(
    ({ position }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildShapeOrientation(position, 'circle') },
        reason: 'change-radial-position',
        phase: 'change',
      });
    },
    [handleModelChange]
  );

  const handleRadialCenterCommit = useCallback(
    ({ position }) => {
      handleModelChange({
        patch: { orientation: gradientModelManager.buildShapeOrientation(position, 'circle') },
        reason: 'change-radial-position',
        phase: 'end',
      });
    },
    [handleModelChange]
  );

  const handleControlPanelChange = useCallback(
    (payload) => {
      handleModelChange({
        patch: (prev) =>
          colorStopsManager.setPosition(prev.colorStops, payload.id, payload.position),
        reason: 'change-stop-position',
        phase: 'end',
      });
    },
    [handleModelChange]
  );

  const handleControlPanelClick = useCallback(
    (payload) => {
      if (!onActiveColorClick) {
        return;
      }

      const stop = model.colorStops.find((s) => s.id === payload.colorStop.id);

      if (stop) {
        onActiveColorClick({ colorStopId: stop.id, colorStop: stop });
      }
    },
    [model.colorStops, onActiveColorClick]
  );

  const handleFlipAction = useCallback(() => {
    handleModelChange({
      patch: (prev) => colorStopsManager.mirror(prev.colorStops),
      reason: 'flip-stops',
      phase: 'end',
    });
  }, [handleModelChange]);

  const handleDeleteAction = useCallback(() => {
    handleModelChange({
      patch: (prev) => colorStopsManager.remove(prev.colorStops, prev.activeColorStopId),
      reason: 'delete-stop',
      phase: 'end',
    });
  }, [handleModelChange]);

  const gradientTypeOptions = useMemo(
    () => [
      {
        value: 'linear-gradient',
        label: captions.linearOptionLabel,
      },
      {
        value: 'radial-gradient',
        label: captions.radialOptionLabel,
      },
    ],
    [captions.linearOptionLabel, captions.radialOptionLabel]
  );
  const activeGradientTypeOption = useMemo(
    () =>
      gradientTypeOptions.find((option) => option.value === model.type) ?? gradientTypeOptions[0],
    [gradientTypeOptions, model.type]
  );

  const actions = useMemo(
    () => [
      {
        id: 'flip',
        glyph: 'design-switch',
        label: captions.flipActionLabel,
        onClick: handleFlipAction,
      },
      {
        id: 'delete',
        glyph: 'trash',
        label: captions.deleteActionLabel,
        disabled: model.colorStops.length <= 1,
        onClick: handleDeleteAction,
      },
    ],
    [
      captions.deleteActionLabel,
      captions.flipActionLabel,
      handleDeleteAction,
      handleFlipAction,
      model.colorStops.length,
    ]
  );

  return (
    <div ref={pickerRef} className="ws-ui-gradient-picker">
      <Box paddingLeft="spacing-m" paddingRight="spacing-m" marginBottom="spacing-2-xs">
        <SimpleSelect
          value={activeGradientTypeOption}
          options={gradientTypeOptions}
          onChange={handleGradientTypeChange}
        />
      </Box>
      <div className="ws-ui-gradient-picker__preview">
        {isLinearVariant ? (
          <LinearGradientAnglePicker
            value={linearAngle}
            background={previewBackground}
            isDarkTheme={isAnglePickerDarkTheme}
            onDragStart={handleLinearAngleDragStart}
            onDrag={handleLinearAngleDrag}
            onDragEnd={handleLinearAngleCommit}
            onClick={handleLinearAngleCommit}
            onInputChange={handleLinearAngleCommit}
          />
        ) : (
          <RadialGradientCenterPicker
            position={radialPosition}
            background={previewBackground}
            onDragStart={handleRadialCenterDragStart}
            onDrag={handleRadialCenterDrag}
            onDragEnd={handleRadialCenterCommit}
            onClick={handleRadialCenterCommit}
            onKeyboardChange={handleRadialCenterCommit}
          />
        )}
      </div>
      <Box
        paddingTop="spacing-2-xs"
        paddingBottom="spacing-3-xs"
        paddingLeft="spacing-m"
        paddingRight="spacing-m"
      >
        <HorizontalStack gap="spacing-2-xs">
          <div ref={sliderMeasureRef} className="ws-ui-gradient-picker__slider">
            <GradientStopsSlider
              background={sliderBackground}
              stops={sliderStops}
              activeStopId={model.activeColorStopId}
              onDragStart={handleStopsDragStart}
              onDrag={handleStopsDrag}
              onDragEnd={handleStopsDragEnd}
              onRailClick={handleRailClick}
            />
          </div>
          <div className="ws-ui-gradient-picker__actions">
            <GradientStopActions actions={actions} />
          </div>
        </HorizontalStack>
      </Box>
      {activeColorStop && (
        <Box paddingLeft="spacing-2-xs" paddingRight="spacing-2-xs">
          <div ref={panelMeasureRef} className="ws-ui-gradient-picker__color-stop-panel">
            <ColorStopControlPanel
              value={activeColorStop}
              title={captions.colorStopControlTitle}
              popoverProps={{
                width: 246,
                content: captions.colorStopControlPopoverContent,
              }}
              arrowPosition={arrowPosition}
              onChange={handleControlPanelChange}
              onClick={onActiveColorClick ? handleControlPanelClick : undefined}
            />
          </div>
        </Box>
      )}
    </div>
  );
};

GradientPicker.displayName = 'GradientPicker';

export default GradientPicker;
